import { useEffect, useMemo, useRef, useState } from 'react';

import Icon from '@klv/atoms/icon';
import useOutsideClick from '@klv/hooks/useOutsideClick';

import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/dropdownButton.module.scss';

import type { IDropdownButton } from './types';

export const DropdownButton = ({
  children,
  className,
  classNames,
  dropdownItems,
  dropdownLabel,
  dropdownName,
  menuPosition,
  onDropdownItemKeyDown,
  showDivider = true,
  variant = 'primary',
  ...props
}: IDropdownButton) => {
  const [open, setOpen] = useState(false);
  const [hasAnchorChild, setHasAnchorChild] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLLIElement | null>(null);
  const childContainerRef = useRef<HTMLDivElement | null>(null);

  const handleKeyUp = useMemo(() => (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (childContainerRef.current) {
      setHasAnchorChild(
        childContainerRef.current.childNodes.item(0)?.nodeName === 'A',
      );
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyUp, open]);

  useOutsideClick(ref, () => {
    setOpen(false);
  });

  const toggleDropdown = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement> | undefined) => {
    event?.preventDefault();
    setOpen((val) => !val);
  };

  const handleOnButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(prev => !prev);
      if (!open) {
        setTimeout(() => {
          firstItemRef.current?.focus();
        }, 0);
      }
    }   
    if (event.shiftKey && event.key === 'Tab') {
      setOpen(false);
    }
    if (!event.shiftKey && event.key === 'Tab') {
      setOpen(true);
    }
  };

  const handleOnItemKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, index: number) => {
    const isLastItem = index === dropdownItems.length - 1;
    if (!event.shiftKey && event.key === 'Tab' && isLastItem) {
      setOpen(false);
    }
  };

  const { button, dropdown, icon, label, root } = classNames || {};
  const { top } = menuPosition || {};

  const buttonClasses = generateClassName([
    className,
    root,
    variant === 'transparent' ? styles.transparent : styles.primary,
    styles.dropdownButtonRoot,
    open && styles.open,
  ]);

  const eventProps = {
    onClick: toggleDropdown,
    onKeyDown: handleOnButtonKeyDown,
    tabIndex: 0,
  };

  return (
    <div
      style={{ 
        ...(top && { '--menu-top-position': top }),
      } as React.CSSProperties}
      className={buttonClasses}
      ref={ref}
      {...props}
    >
      <button
        aria-controls={dropdownName}
        aria-label='open menu'
        aria-expanded={open}
        aria-haspopup={open}
        className={generateClassName([button, styles.dropdownButton])}
        {...(hasAnchorChild ? {} : { ...eventProps })}
      >
        <div ref={childContainerRef} className={generateClassName([label, styles.children])}>
          {children}
        </div>
        {showDivider && <span>|</span>}
        <div
          className={generateClassName([icon, styles.dropdownIcon])}
          {...(hasAnchorChild ? { ...eventProps } : {})}
        >
          <Icon fillColor='currentColor' iconName='ChevronDown' size={0.6} />
        </div>
      </button>
      <div className={generateClassName([styles.dropdown, dropdown])} id={dropdownName}>
        <ul className='formLarge'>
          {dropdownLabel && (
            <li tabIndex={-1}>
              {dropdownLabel}
            </li>
          )}
          {dropdownItems.map((item, index) => (
            <li
              key={index}
              ref={index === 0 ? firstItemRef : null}
              tabIndex={0}
              onKeyDown={(event) => {
                if (onDropdownItemKeyDown) {
                  onDropdownItemKeyDown(event, index);
                }
                handleOnItemKeyDown(event, index);
              }}
            >
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
